import { Component, createRef } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, IconButton } from '@mui/material';
import bb from 'billboard.js/dist/billboard.pkgd';
import 'billboard.js/dist/billboard.css';
import moment from 'moment';

import BarChart from './BarChart';
import styles, { Statuses } from './styles';


class AssignmentChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      otd_stats: [],
      isCloseCharts: false,
      isOpenAssignmentDetail: false,
    };
    this.chartRef = createRef();
    this.chart = null;
  }

  renderChart(stats = {}) {
    const { unassigned = 0, pending = 0, inprogress = 0, failed = 0, succeeded = 0 } = stats || {};

    // Initialize chart if not yet created and ref is available
    if (!this.chart && this.chartRef.current) {
      this.chart = bb.generate({
        bindto: this.chartRef.current,
        size: {
          width: 160,
          height: 200,
        },
        data: {
          labels: true,
          x: undefined,
          order: null,
          columns: [
            ['unassigned', unassigned],
            ['pending', pending],
            ['in_progress', inprogress],
            ['failed', failed],
            ['succeeded', succeeded],
          ],
          type: 'donut',
          colors: {
            unassigned: '#bebfc0',
            pending: '#fa6725',
            in_progress: '#fbc04f',
            succeeded: '#66b752',
            failed: '#d63031',
          },
          names: {
            unassigned: 'Unassigned',
            pending: 'Pending',
            in_progress: 'In Progress',
            succeeded: 'Succeeded',
            failed: 'Failed',
          },
        },
        tooltip: {
          grouped: false,
          format: {
            value: (value, ratio, id, index) => `${(ratio * 100).toFixed(1)}% [${value}]`,
          },
        },
        donut: {
          label: {
            show: false,
          },
        },
        legend: {
          // amount of padding to put between each legend element
          padding: 5,
          item: {
            tile: {
              width: 10,
              height: 10,
            },
          },
        },
      });
    } else if (this.chart) {
      // Only update if chart exists
      this.chart.load({
        columns: [
          ['unassigned', unassigned],
          ['pending', pending],
          ['in_progress', inprogress],
          ['succeeded', succeeded],
          ['failed', failed],
        ],
      });
    }
    // If neither condition is met (chart is null and ref is not ready), skip updating
  }

  computeOtdStats(stats = {}) {
    const { early = 0, late = 0, failed = 0, succeeded = 0 } = stats || {};
    return [
      {
        value: early,
        title: 'Early',
        color: '#a5ccec',
      },
      {
        value: Math.max(0, succeeded - early - late),
        title: 'On Time',
        color: '#66b752',
      },
      {
        value: late,
        title: 'Late',
        color: '#8447f6',
      },
      {
        value: failed,
        title: 'Failed',
        color: '#d63031',
      },
    ];
  }

  componentDidMount() {
    const { stats = {} } = this.props;
    this.setState({ otd_stats: this.computeOtdStats(stats) });
    this.renderChart(stats);
  }

  componentDidUpdate(prevProps) {
    const { stats } = this.props;
    if (stats !== prevProps.stats) {
      this.setState({ otd_stats: this.computeOtdStats(stats) });
      this.renderChart(stats);
    }
  }

  componentWillUnmount() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  toggleAssignmentDetail = () => {
    this.setState({ isOpenAssignmentDetail: !this.state.isOpenAssignmentDetail });
  };

  onToggleChart = () => {
    this.props.onCollapse?.();
    this.setState({ isCloseCharts: !this.state.isCloseCharts });
  };

  render() {
    const { isOpenAssignmentDetail, isCloseCharts } = this.state;
    const { date, stats = {} } = this.props;
    const {
      total = 0,
      succeeded = 0,
      failed = 0,
      early = 0,
      inprogress = 0,
      late = 0,
      pending = 0,
      unassigned = 0,
    } = stats;
    const statsList = [
      { statusColor: 'default', label: 'TOTAL SHIPMENTS', value: total },
      { statusColor: 'default', label: 'TOTAL COMPLETED', value: succeeded + failed },
      { statusColor: 'early', label: 'EARLY', value: early },
      { statusColor: 'failed', label: 'FAILED', value: failed },
      { statusColor: 'in_progress', label: 'IN PROGRESS', value: inprogress },
      { statusColor: 'late', label: 'LATE', value: late },
      { statusColor: 'pending', label: 'PENDING', value: pending },
      { statusColor: 'succeeded', label: 'SUCCEEDED', value: succeeded },
      { statusColor: 'unassigned', label: 'UNASSIGNED', value: unassigned },
    ];

    return (
      <Grid container direction="column" wrap="nowrap" sx={styles.container}>
        <Grid sx={styles.title}>
          {!isCloseCharts && (
            <IconButton
              onClick={this.toggleAssignmentDetail}
              color={isOpenAssignmentDetail ? 'primary' : 'default'}
              size="small"
            >
              <InfoIcon fontSize='small' />
            </IconButton>
          )}
          <Box sx={{ ...styles.innerTitle, textAlign: !isCloseCharts ? 'center' : 'left' }}>
            {`Shipment Breakdown`}
            <Box component="span" sx={{ ...styles.dateTitle, display: !isCloseCharts ? 'block' : 'inline-block' }}>
              {moment(date).format('ddd MMMM DD')}
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={this.onToggleChart}
            aria-expanded={!isCloseCharts}
            sx={styles.toggleChartLink}
          >
            {!isCloseCharts ? `Hide chart` : `Show chart`}
          </Box>
        </Grid>
        <Grid style={{ ...{ display: !isCloseCharts ? 'block' : 'none' } }}>
          <Grid container sx={{ ...styles.panelContainer, display: isOpenAssignmentDetail ? 'flex' : 'none' }}>
            <Box sx={styles.innerPanelContainer}>
              {statsList.map((stat, idx) => (
                <Box sx={styles.rowTitle} key={`stat-${idx}`}>
                  <Box component="label" sx={{ ...styles.rowLabel, color: Statuses[stat.statusColor] }}>
                    {stat.label}:
                  </Box>
                  {stat.value}
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid sx={{ ...styles.panelContainer, display: isOpenAssignmentDetail ? 'none' : 'flex' }}>
            <Box sx={styles.pane}>
              <Box sx={styles.info}>
                total shipments
                <br />
                <Box component="span" sx={styles.shipmentTotal}>{total}</Box>
              </Box>
              <Box ref={this.chartRef} sx={styles.chart} />
            </Box>
            <Box sx={styles.pane}>
              <Box sx={styles.info}>
                total completed
                <br />
                <Box component="span" sx={styles.shipmentTotal}>{succeeded + failed}</Box>
              </Box>
              <BarChart height="160px" width={30} data={this.state.otd_stats} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

export default AssignmentChart;
